import { useCallback, useEffect, useState } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { apiEndpoints } from '../utils/api';

interface UseScanBranchesResult {
  branches: string[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Fetch the distinct branches scanned in the current organization (optionally scoped to a
 * single application). Powers the branch filter dropdown on the Scans and Findings pages.
 */
export function useScanBranches(applicationId?: string | null): UseScanBranchesResult {
  const api = useAuthenticatedApi();
  const [branches, setBranches] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBranches = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<string[]>(apiEndpoints.scans.branches(applicationId || undefined));
      // Defensive: never let a malformed (non-array) response crash consumers that map over branches.
      setBranches(Array.isArray(response) ? response : []);
    } catch (err: any) {
      console.error('Error fetching scan branches:', err);
      setError(err?.message || 'Failed to load branches');
      setBranches([]);
    } finally {
      setIsLoading(false);
    }
  }, [api, applicationId]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  return { branches, isLoading, error };
}

export default useScanBranches;
